import config from '../config.json';
import { User } from '../types/user';
import { getAuthToken, setAuthToken, removeAuthToken } from './auth-storage';
import { api, initializeAuthHeader } from '../lib/axios';

// Re-export the api instance
export { api };

// Authentication service
export const authService = {
  async login(email: string, password: string) {
    //console.log('Logging in with:', { email });
    
    // Use consistent /api prefix for all endpoints
    const loginPath = '/api/auth/login';
    //console.log('Using login path:', loginPath);
    
    try {
      const response = await api.post<{token: string; user: User}>(loginPath, { email, password });
      //console.log('Login response received:', response.status);
      
      if (!response.data || !response.data.token) {
        console.error('Login response missing token:', response.data);
        throw new Error('No authentication token received');
      }
      
      const { token, user } = response.data;
      
      //console.log('Received token from server, setting in session storage');
      
      // Store token in session storage
      setAuthToken(token);
      
      // Set token for all future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      //console.log('Login successful, token set in session storage and authorization header');
      
      // Verify token was set properly
      const storedToken = getAuthToken();
      if (!storedToken) {
        console.error('Token not saved correctly in session storage!');
      }
      
      return { token, user };
    } catch (error: any) {
      console.error('Login error:', error.response?.status, error.response?.data);
      throw error;
    }
  },

  async register(email: string, password: string, fullName: string, invitationKey: string) {
    // Validate required params
    if (!email || !password || !fullName || !invitationKey) {
      console.error('Missing required parameters for registration:', {
        hasEmail: !!email,
        hasPassword: !!password,
        hasFullName: !!fullName,
        hasInvitationKey: !!invitationKey
      });
      throw new Error('Missing required registration information');
    }
    
    // Commented out console log to prevent syntax errors
    /*
    console.log('Registering user with data:', {
      email,
      fullName,
      invitationKey: invitationKey ? `${invitationKey.substring(0, 3)}...` : 'missing' // Show only first 3 chars
    });
    */
    
    try {
      const response = await api.post('/api/auth/register', {
        email,
        password,
        fullName,
        invitationKey,
      });
      
      //console.log('Registration response status:', response.status);
      //console.log('Registration response data:', response.data);
      
      return response.data;
    } catch (error: any) {
      console.error('Registration error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      // Check for specific error types
      if (error.response?.status === 400) {
        throw new Error(
          error.response?.data?.message || 
          'Invalid registration data. Please check your information and try again.'
        );
      } else if (error.response?.status === 404) {
        throw new Error('Invalid invitation key or invitation has expired.');
      }
      
      // Generic error
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Unknown error during registration'
      );
    }
  },

  async completeRegistration(email: string, password: string, fullName: string, invitationKey: string) {
    // Validate required params
    if (!email || !password || !fullName || !invitationKey) {
      console.error('Missing required parameters for registration:', {
        hasEmail: !!email,
        hasPassword: !!password,
        hasFullName: !!fullName,
        hasInvitationKey: !!invitationKey
      });
      throw new Error('Missing required registration information');
    }
    
    try {
      const response = await api.post<{
        message: string;
        token: string;
        user: {
          id: string;
          name: string;
          email: string;
          role: string;
          isAdmin: boolean;
          fullName: string;
          organization: {
            id: string;
            name: string;
            role: string;
          };
        };
      }>('/api/auth/complete-registration', {
        email,
        password,
        fullName,
        invitationKey,
      });
      
      const { token, user } = response.data;
      
      // Store the token in session storage
      setAuthToken(token);
      
      // Set token for all future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      return { 
        success: true, 
        token,
        user
      };
    } catch (error: any) {
      console.error('Registration error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.status === 400) {
        throw new Error(
          error.response?.data?.message || 
          'Invalid registration data. Please check your information and try again.'
        );
      } else if (error.response?.status === 404) {
        throw new Error('Invalid invitation key or invitation has expired.');
      }
      
      return { 
        success: false, 
        message: error.response?.data?.message || error.message || 'Registration failed' 
      };
    }
  },

  async resetPassword(email: string) {
    const response = await api.post('/api/auth/reset-password', { email });
    return response.data;
  },

  async confirmResetPassword(email: string, token: string, newPassword: string) {
    const response = await api.post<{ message: string }>('/api/auth/confirm-reset-password', {
      email,
      token,
      newPassword,
    });
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    //console.log('Getting current user with token:', !!getAuthToken());
    try {
      // Use consistent /api prefix for all endpoints
      const userPath = '/api/user/me';
      //console.log('Using user path:', userPath);
      
      const response = await api.get<User>(userPath);
      //console.log('getCurrentUser response:', response.data);
      return response.data;
    } catch (error) {
      console.error('getCurrentUser error:', error);
      throw error;
    }
  },

  async validateInvitationKey(key: string) {
    try {
      interface ValidationResponse {
        valid: boolean;
        message?: string;
        organizationId: string;
        organizationName: string;
        expiresAt?: string;
      }
      
      const response = await api.post<ValidationResponse>('/api/invitation/validate', { key });
      
      // Return the response data directly, preserving all properties from the server
      if (response.data) {
        return {
          valid: response.data.valid === true,
          message: response.data.message || 'Invitation validated',
          organizationId: response.data.organizationId,
          organizationName: response.data.organizationName,
          expiresAt: response.data.expiresAt,
          // Keep data property for backward compatibility
          data: {
            organizationId: response.data.organizationId,
            organizationName: response.data.organizationName,
            expiresAt: response.data.expiresAt
          }
        };
      }
      
      return {
        valid: false,
        message: 'Invalid response format',
        data: null
      };
    } catch (error: any) {
      console.error('Error validating invitation key:', error);
      return {
        valid: false,
        message: error.response?.data?.message || error.message || 'Failed to validate invitation',
        data: null
      };
    }
  },

  logout() {
    // Clear token from session storage
    removeAuthToken();
    // Clear all sessionStorage
    sessionStorage.clear();
    // Clear auth header
    delete api.defaults.headers.common['Authorization'];
  },
};

// Organization service
export const organizationService = {
  async getOrganizations() {
    const response = await api.get('/api/organization');
    return response.data;
  },

  async getOrganization(id: string) {
    const response = await api.get(`/api/organization/${id}`);
    return response.data;
  },

  async createOrganization(name: string, description?: string, labels?: string[]) {
    const response = await api.post('/api/organization', { name, description, labels });
    return response.data;
  },

  async updateOrganization(id: string, name: string, description?: string, labels?: string[]) {
    const response = await api.put(`/api/organization/${id}`, { name, description, labels });
    return response.data;
  },

  async deleteOrganization(id: string) {
    await api.delete(`/api/organization/${id}`);
  },

  async addUserToOrganization(organizationId: string, userId: string) {
    const response = await api.post(`/api/organization/${organizationId}/users/${userId}`);
    return response.data;
  },

  async removeUserFromOrganization(organizationId: string, userId: string) {
    await api.delete(`/api/organization/${organizationId}/users/${userId}`);
  },
};

// Invitation service
export const invitationService = {
  async getOrganizationInvitations(organizationId: string) {
    const response = await api.get(`/api/invitation/organization/${organizationId}`);
    return response.data;
  },

  async generateInvitationKey(organizationId: string, expiresAt?: Date) {
    const response = await api.post(`/api/invitation/organization/${organizationId}/generate`, {
      expiresAt,
    });
    return response.data;
  },

  async deleteInvitationKey(id: string) {
    await api.delete(`/api/invitation/${id}`);
  },

  async validateInvitationKey(key: string) {
    const response = await api.post('/api/invitation/validate', { key });
    return response.data;
  },
};

// Course service
export const courseService = {
  async getCourses() {
    const response = await api.get('/api/course');
    return response.data;
  },

  async getCourse(id: string) {
    const response = await api.get(`/api/course/${id}`);
    return response.data;
  },

  async createCourse(title: string, description?: string) {
    const response = await api.post('/api/course', { title, description });
    return response.data;
  },

  async updateCourse(id: string, title: string, description?: string) {
    const response = await api.put(`/api/course/${id}`, { title, description });
    return response.data;
  },

  async deleteCourse(id: string) {
    await api.delete(`/api/course/${id}`);
  },

  async getCourseProgress(id: string) {
    const response = await api.get(`/api/course/${id}/progress`);
    return response.data;
  },

  async getCourseCertificate(id: string) {
    const response = await api.get(`/api/course/${id}/certificate`);
    return response.data;
  },
  
  async getLabels() {
    const response = await api.get('/api/course/labels');
    return response.data;
  },
};

// Lesson service
export const lessonService = {
  async getLessons() {
    const response = await api.get('/api/lesson');
    return response.data;
  },

  async getLesson(id: string) {
    const response = await api.get(`/api/lesson/${id}`);
    return response.data;
  },

  async createLesson(title: string, content: string, courseId: string, orderIndex: number, quizQuestions: any[] = []) {
    const response = await api.post('/api/lesson', {
      title,
      content,
      courseId,
      orderIndex,
      quizQuestions
    });
    return response.data;
  },

  async updateLesson(id: string, title: string, content: string, orderIndex: number, quizQuestions: any[] = []) {
    const response = await api.put(`/api/lesson/${id}`, {
      title,
      content,
      orderIndex,
      quizQuestions
    });
    return response.data;
  },

  async deleteLesson(id: string) {
    await api.delete(`/api/lesson/${id}`);
  },

  async completeLesson(id: string) {
    const response = await api.post(`/api/lesson/${id}/complete`);
    return response.data;
  },

  async getLessonProgress(id: string) {
    const response = await api.get(`/api/lesson/${id}/progress`);
    return response.data;
  },
};

// Quiz service
export const quizService = {
  async getLessonQuiz(lessonId: string) {
    const response = await api.get(`/api/quiz/lesson/${lessonId}`);
    return response.data;
  },

  async createQuizQuestion(lessonId: string, questionText: string, answers: Array<{ text: string; isCorrect: boolean }>) {
    const response = await api.post(`/api/quiz/lesson/${lessonId}/questions`, {
      questionText,
      answers,
    });
    return response.data;
  },

  async updateQuizQuestion(id: string, questionText: string, answers: Array<{ text: string; isCorrect: boolean }>) {
    const response = await api.put(`/api/quiz/questions/${id}`, {
      questionText,
      answers,
    });
    return response.data;
  },

  async deleteQuizQuestion(id: string) {
    await api.delete(`/api/quiz/questions/${id}`);
  },

  async submitQuizAnswer(questionId: string, answerId: string) {
    const response = await api.post(`/api/quiz/questions/${questionId}/submit`, {
      answerId,
    });
    return response.data;
  },
};

export const progressService = {
  async getCourseProgress(courseId: string) {
    const response = await api.get(`/api/progress/course/${courseId}`);
    return response.data;
  },

  async getLessonProgress(lessonId: string) {
    const response = await api.get(`/api/progress/lesson/${lessonId}`);
    return response.data;
  },

  async completeLesson(lessonId: string) {
    const response = await api.post(`/api/progress/lesson/${lessonId}/complete`);
    return response.data;
  },

  async getProgressOverview() {
    const response = await api.get('/api/progress/overview');
    return response.data;
  },

  async getProgressStats() {
    const response = await api.get('/api/progress/stats');
    return response.data;
  },
};

export interface FileInfo {
  fileName: string;
  originalName: string;
  size: number;
  contentType: string;
  uploadDate: string;
  url: string;
}

export const fileService = {
  async uploadVideo(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/file/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async getVideo(fileName: string) {
    const response = await api.get(`/api/file/${fileName}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async deleteVideo(fileName: string) {
    await api.delete(`/api/file/${fileName}`);
  },
  
  // New methods for general file management
  async uploadFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/file/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async getFile(fileName: string) {
    const response = await api.get(`/api/file/${fileName}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async deleteFile(fileName: string) {
    await api.delete(`/api/file/${fileName}`);
  },
  
  async listFiles(): Promise<FileInfo[]> {
    const response = await api.get<FileInfo[]>('/api/file/list');
    return response.data;
  },
  
  getMarkdownLink(file: FileInfo): string {
    // If it's an image, return markdown image syntax
    if (file.contentType.startsWith('image/')) {
      return `![${file.originalName}](${file.url})`;
    }
    // Otherwise, return a regular markdown link
    return `[${file.originalName}](${file.url})`;
  }
};

/**
 * Stats API Service
 */
export const statsService = {
  async getProfileStats() {
    try {
      const response = await api.get<{
        totalCourses: number;
        totalCompleted: number;
        totalLessonsCompleted: number;
      }>('/api/stats/ProfileStats');
      return response.data;
    } catch (error) {
      console.error('Error fetching profile stats:', error);
      throw error;
    }
  },
  
  async getDashboardStats() {
    try {
      const response = await api.get<{
        totalUsers: number;
        totalOrganizations: number;
        totalCourses: number;
        totalCompleted: number;
        totalLessonsCompleted: number;
      }>('/api/stats/DashboardStats');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }
};